import React, { useEffect, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { useReducedMotion } from "@mantine/hooks";
import { Skeleton } from "@mantine/core";
import css from "./OrganizationSummary.module.scss";

export const number = (value) => Number.isFinite(value);
export const hours = (value) =>
  number(value) ? `${Math.max(0, value).toFixed(1)}h` : "—";
export const money = (value) =>
  number(value)
    ? new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        notation: "compact",
        minimumFractionDigits: 0,
        maximumFractionDigits: 1,
      }).format(value)
    : "—";

export function TimeTrend({
  values,
  current,
  label,
  onClick,
  disabled,
  increaseIsGood = false,
  historyPending = false,
  format = hours,
  actionLabel = "View meeting time details",
}) {
  const previous = values[values.length - 1];
  const increased = current > previous;
  const changeClass =
    current === previous
      ? undefined
      : increased === increaseIsGood
        ? css.positiveChange
        : css.negativeChange;
  const points = [...values, current];
  const complete = points.length === 6 && points.every(number);
  const reducedMotion = useReducedMotion();
  const previousPoints = useRef([]);
  useEffect(() => {
    if (complete && !historyPending) previousPoints.current = points;
  });
  const displayedPoints = historyPending ? previousPoints.current : points;
  const showChart =
    displayedPoints.length === 6 && displayedPoints.every(number);
  const color = increaseIsGood ? "#8b8e9e" : "#39a8f0";
  const chart = (
    <div
      className={css.sparkline}
      role="img"
      aria-label={`${label} over six periods`}
      aria-busy={historyPending}
      style={{ opacity: historyPending ? 0.5 : 1 }}
    >
      <ReactECharts
        style={{ width: "100%", height: "100%", pointerEvents: "none" }}
        opts={{ renderer: "svg" }}
        notMerge={false}
        option={{
          animation: !reducedMotion,
          animationDurationUpdate: 550,
          animationEasingUpdate: "cubicInOut",
          grid: { left: 4, right: 4, top: 4, bottom: 4 },
          xAxis: {
            type: "category",
            show: false,
            boundaryGap: false,
            data: [0, 1, 2, 3, 4, 5],
          },
          yAxis: { type: "value", show: false, scale: true },
          series: [
            {
              id: "trend",
              type: "line",
              silent: true,
              data: displayedPoints,
              lineStyle: { color, width: 2 },
              itemStyle: { color },
              symbol: "circle",
              symbolSize: (_, params) => (params.dataIndex === 5 ? 6 : 0),
            },
          ],
        }}
      />
    </div>
  );
  return (
    <>
      {showChart &&
        (onClick ? (
          <button
            type="button"
            className={css.sparklineButton}
            aria-label={actionLabel}
            onClick={onClick}
            disabled={disabled}
          >
            {chart}
          </button>
        ) : (
          chart
        ))}
      {!historyPending && number(previous) && number(current) && (
        <div className={css.caption}>
          {previous > 0 && (
            <strong className={changeClass}>
              {current > previous ? "↑" : current < previous ? "↓" : ""}{" "}
              {Math.abs(((current - previous) / previous) * 100).toFixed(0)}
              %{" "}
            </strong>
          )}
          {previous === 0 ? "Previously " : "from "}
          {format(previous)}
        </div>
      )}
    </>
  );
}

export function MetricCard({ title, loading, children, description }) {
  return (
    <section className={css.card} aria-label={title} aria-busy={loading}>
      <h3 title={description}>{title}</h3>
      {loading ? <Skeleton height={65} mt={12} /> : children}
    </section>
  );
}

// A two-part bar with its labels. Each part opens that part's meetings.
export function InternalExternalSplit({
  internal,
  external,
  format,
  noun,
  ariaLabel,
  uid,
  onOpen,
}) {
  const sum = (internal || 0) + (external || 0);
  const parts = [
    { name: "internal", type: "Internal", value: internal },
    { name: "external", type: "External", value: external },
  ];
  return (
    <>
      <div className={css.costBar} role="group" aria-label={ariaLabel}>
        {parts.map(({ name, type, value }) => (
          <button
            key={name}
            type="button"
            aria-label={`View ${name} ${noun}: ${format(value)}`}
            disabled={!uid || !(value > 0)}
            onClick={() => onOpen(name, type)}
            style={{ width: `${sum > 0 ? ((value || 0) / sum) * 100 : 0}%` }}
          />
        ))}
      </div>
      <div className={css.costLabels}>
        {parts.map(({ name, type, value }) => (
          <button
            key={name}
            disabled={!uid || !number(value)}
            onClick={() => onOpen(name, type)}
          >
            {format(value)} {name}
          </button>
        ))}
      </div>
    </>
  );
}

export function AverageRatingCard({ rating, loading, uid, onOpenStars }) {
  const buckets = [1, 2, 3, 4, 5].map(
    (star) => rating?.ratings?.[`${star} star`]?.count ?? 0,
  );
  const count = buckets.reduce((sum, value) => sum + value, 0);
  const maxCount = Math.max(...buckets, 1);
  return (
    <MetricCard title="Average rating" loading={loading}>
      <div className={css.value}>
        {number(rating?.totalAverageOfRatings) &&
        (count > 0 || !rating?.ratings)
          ? rating.totalAverageOfRatings.toFixed(1)
          : "—"}
        <small> / 5</small>
      </div>
      {rating?.ratings && count > 0 && (
        <div className={css.ratings} aria-label="Rating distribution">
          {buckets.map((value, index) => (
            <button
              key={index}
              disabled={!uid || !value}
              title={`${index + 1} stars: ${value} ratings`}
              aria-label={`${index + 1} stars: ${value} ratings`}
              onClick={() => onOpenStars(index + 1)}
              type="button"
            >
              <span className={css.ratingTrack} aria-hidden="true">
                <span
                  className={css.ratingBar}
                  style={{
                    height: `${value ? Math.max(8, (value / maxCount) * 100) : 0}%`,
                  }}
                />
              </span>
            </button>
          ))}
        </div>
      )}
      <div className={css.caption}>
        {!rating
          ? "Rating data unavailable"
          : !rating.ratings
            ? "Awaiting refreshed rating breakdown"
            : count
              ? `${count} ${count === 1 ? "rating" : "ratings"}`
              : "No ratings yet"}
      </div>
    </MetricCard>
  );
}

export function MeetingCostCard({
  cost,
  loading,
  uid,
  onOpen,
  history = [],
  historyPending,
}) {
  return (
    <MetricCard title="Meeting cost" loading={loading}>
      <button
        className={css.value}
        disabled={!uid || !number(cost?.totalMeetingsCost)}
        onClick={() => onOpen("total", "All")}
      >
        {money(cost?.totalMeetingsCost)}
      </button>
      <TimeTrend
        label="Meeting cost"
        actionLabel="View meeting cost details"
        format={money}
        historyPending={historyPending}
        disabled={!uid || !number(cost?.totalMeetingsCost)}
        onClick={() => onOpen("total", "All")}
        values={history}
        current={cost?.totalMeetingsCost}
      />
      <InternalExternalSplit
        internal={cost?.internalMeetingsCost}
        external={cost?.externalMeetingsCost}
        format={money}
        noun="meeting costs"
        ariaLabel="Meeting cost breakdown"
        uid={uid}
        onOpen={onOpen}
      />
    </MetricCard>
  );
}
